import {
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";

import { useTheme } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { Button, Form, SubmitButton } from "@covertree/ui";

import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationSchemaForCoverageDataForm, CoverageFormData } from "@forms";

import { CoverageDataForm } from "./CoverageDataForm";
import CoverageHomeDetailsForm from "./CoverageHomeDetailsForm";
import {
  addExternalCoverageData,
  selectExternalCoverageData,
} from "@redux/coverages/coveragesSlice";
import {
  selectEventEditableFields,
  selectEventType,
  selectPreEventCoverage,
} from "@redux/event/eventSlice";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import { useAppSelector } from "@redux/hooks/useAppSelector";
import {
  ADD_EVENT_ROUTES,
  PROOF_OF_COVERAGE_ROUTES,
} from "@routing/Residents/ProofOfCoverage";
import { moveBackInPathname } from "@utils";
import { Stack } from "@mui/material";

interface ProofOfCoverageFormProps {
  isAddingEvent: boolean;
}

export default function ProofOfCoverageForm({
  isAddingEvent,
}: ProofOfCoverageFormProps) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const externalPolicyData = useAppSelector(selectExternalCoverageData);
  const preEventCoverage = useAppSelector(selectPreEventCoverage);

  const eventType = useAppSelector(selectEventType);
  const eventEditableFields = useAppSelector(selectEventEditableFields);

  const form = useForm({
    defaultValues: {
      ...externalPolicyData,
      agencyName: externalPolicyData.agencyName || undefined,
      agencyAddress: externalPolicyData.agencyAddress || undefined,
      description: externalPolicyData.description || undefined,
      policyEffectiveDate: externalPolicyData.policyEffectiveDate
        ? new Date(externalPolicyData.policyEffectiveDate)
        : undefined,
      policyEndDate: externalPolicyData.policyEndDate
        ? new Date(externalPolicyData.policyEndDate)
        : undefined,
    },
    mode: "all",
    resolver: yupResolver(validationSchemaForCoverageDataForm),
  });

  const {
    handleSubmit,
    setValue,
    trigger,
    getValues,
    watch,
    formState: { isSubmitting, isValid },
  } = form;

  const homeDetailsOpen = watch("withHomeDetails");

  const toggleHomeDetailsOpen = () => {
    setValue("withHomeDetails", !homeDetailsOpen);

    const { manufacturer, modelYear } = getValues();

    if (!manufacturer) {
      setValue("manufacturer", externalPolicyData.manufacturer);
    }
    if (!modelYear) {
      setValue("modelYear", externalPolicyData.modelYear);
    }
    trigger(["withHomeDetails", "manufacturer", "modelYear"]);
  };

  const onSubmit = (formData: CoverageFormData) => {
    dispatch(
      addExternalCoverageData({
        ...formData,
        policyEffectiveDate: +formData.policyEffectiveDate,
        policyEndDate: +formData.policyEndDate,
      })
    );

    if (isAddingEvent) {
      navigate({
        pathname: `${moveBackInPathname(location.pathname, 1)}/${ADD_EVENT_ROUTES.EVENT_DOCUMENTS}`,
        search: createSearchParams(searchParams).toString(),
      });
      return;
    }

    navigate({
      pathname: `${moveBackInPathname(location.pathname, 2)}/${PROOF_OF_COVERAGE_ROUTES.COVERAGE_DOCUMENTS}`,
      search: createSearchParams(searchParams).toString(),
    });
  };

  if (isAddingEvent && !eventEditableFields) {
    return (
      <>
        <Typography lineHeight="400%">
          Internal error has occurred. Please try again later.
        </Typography>
      </>
    );
  }

  return (
    <Grid
      container
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      justifyItems="flex-start"
      gap={3}>
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Typography lineHeight={theme.spacing(5)} fontSize={32}>
            {isAddingEvent ? "Event Details" : "Coverage Data"}
          </Typography>
          <CoverageDataForm
            editableFields={eventEditableFields}
            eventType={eventType}
            form={form}
            preEventCoverage={preEventCoverage}
          />
          <Grid
            item
            container
            justifyContent="space-between"
            alignItems="center"
            width="100%"
            mt={3}
            mb={3}
            p={3}
            borderRadius={2}
            sx={
              homeDetailsOpen
                ? {
                    backgroundColor: "secondary.contrastText",
                  }
                : {
                    padding: 1,
                  }
            }>
            <Stack
              direction="row"
              flexWrap="wrap"
              flexGrow={1}
              justifyContent="space-between"
              alignItems="center"
              gap={1}>
              <Typography lineHeight={theme.spacing(5)} fontSize={32}>
                Home Details
                {!homeDetailsOpen && (
                  <Typography
                    ml={1}
                    fontSize={32}
                    color="text.secondary"
                    component="span">
                    {"(Optional)"}
                  </Typography>
                )}
              </Typography>
              {homeDetailsOpen && (
                <Button
                  sx={{ justifySelf: "flex-end" }}
                  variant="text"
                  onClick={toggleHomeDetailsOpen}
                  disabled={
                    eventEditableFields &&
                    !eventEditableFields.allOtherFieldsEditable
                  }
                  disableRipple>
                  Remove
                </Button>
              )}
            </Stack>
            {homeDetailsOpen && (
              <CoverageHomeDetailsForm
                editableFields={eventEditableFields}
                form={form}
              />
            )}
          </Grid>

          {!homeDetailsOpen && (
            <Button
              variant="outlined"
              disabled={
                eventEditableFields &&
                !eventEditableFields.allOtherFieldsEditable
              }
              onClick={toggleHomeDetailsOpen}>
              Add Home Details
            </Button>
          )}

          <Grid item xs={4}>
            <SubmitButton disabled={!isValid} loading={isSubmitting}>
              Continue
            </SubmitButton>
          </Grid>
        </Form>
      </FormProvider>
    </Grid>
  );
}
